import { type Dispatch, type SetStateAction, memo, useMemo } from 'react';
import { type Virtualizer } from '@tanstack/react-virtual';
import { type IngestionAlbum } from '@/hooks/data/inspector/useUploadStore';
import { DAMNodeType, type TreeRecursiveChildrenNode } from '@/types/tree';
import { DEPTH_PADDING, TreeMode } from '@/components/sidebar/tree/tree';
import { Folder } from '@/components/sidebar/tree/folder';
import { Album } from '@/components/sidebar/tree/album';
import { cn } from '@/lib/utils';

type FlattenedTreeItem = {
  treeBranch: TreeRecursiveChildrenNode;
  depth: number;
  parentId?: string;
};

type BranchProps = {
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  flattenedItems: Array<FlattenedTreeItem>;
  tree: Array<TreeRecursiveChildrenNode>;
  openIds: Record<string, boolean>;
  setOpenIds: Dispatch<SetStateAction<Record<string, boolean>>>;
  toggleNodeHandler: (id: string) => void;
  mode?: TreeMode;
  disableDrag?: boolean;
  scrollToNodeId?: string;
  onScrollToNode?: (id: string) => void;
  selectedAlbums?: Array<IngestionAlbum>;
  isSearchResult?: boolean;
  handleSelectFolderCallback: (treeBranch: TreeRecursiveChildrenNode) => void;
  handleSelectAlbumCallback: (treeBranch: TreeRecursiveChildrenNode) => void;
};

const BranchComponent = ({
  virtualizer,
  flattenedItems,
  tree,
  openIds,
  setOpenIds,
  toggleNodeHandler,
  mode,
  disableDrag,
  scrollToNodeId,
  onScrollToNode,
  selectedAlbums,
  isSearchResult = false,
  handleSelectFolderCallback,
  handleSelectAlbumCallback,
}: BranchProps) => {
  const activeDepthsPerItem = useMemo(() => {
    return flattenedItems.map(({ depth }) => {
      return new Set(Array.from({ length: depth }, (_, i) => i));
    });
  }, [flattenedItems]);

  return (
    <ul className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
      {virtualizer.getVirtualItems().map((virtualItem) => {
        const { treeBranch, depth, parentId } = flattenedItems[virtualItem.index];
        const { id, type } = treeBranch;

        const isOpen = !isSearchResult && Boolean(openIds[id]);

        return (
          <li
            key={`${id}-${virtualItem.index}`}
            data-index={virtualItem.index}
            className={cn('absolute left-0 top-0 w-full')}
            style={{
              height: `${virtualItem.size}px`,
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            {activeDepthsPerItem[virtualItem.index] &&
              Array.from(activeDepthsPerItem[virtualItem.index]).map((k) => (
                <div
                  key={k}
                  className="pointer-events-none absolute inset-y-0 z-20 w-px bg-neutral-500/20 opacity-0 transition-opacity duration-300 group-hover/tree:opacity-100"
                  style={{ left: `${k * DEPTH_PADDING + 22}px` }}
                />
              ))}
            {type === DAMNodeType.Folder ? (
              <Folder
                key={id}
                treeBranch={treeBranch}
                tree={tree}
                isOpen={isOpen}
                setOpenIds={setOpenIds}
                toggleNodeHandler={isSearchResult ? () => {} : toggleNodeHandler}
                parentId={isSearchResult ? (treeBranch.node as any)?.parent_id : parentId}
                depth={isSearchResult ? 0 : depth}
                onSelectFolder={() => {
                  handleSelectFolderCallback(treeBranch);
                }}
                mode={mode}
                disableDrag={isSearchResult ? true : disableDrag}
                scrollToNodeId={scrollToNodeId}
                onScrollToNode={onScrollToNode}
                isSearchResult={isSearchResult}
              />
            ) : type === DAMNodeType.Album && mode !== TreeMode.SelectFolder ? (
              <Album
                key={id}
                treeBranch={treeBranch}
                parentId={isSearchResult ? (treeBranch.node as any)?.parent_id : parentId}
                depth={isSearchResult ? 0 : depth}
                mode={mode}
                onSelectAlbum={() => {
                  handleSelectAlbumCallback(treeBranch);
                }}
                disableDrag={isSearchResult ? true : disableDrag}
                selectedAlbums={selectedAlbums}
                isSearchResult={isSearchResult}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

export const Branch = memo(BranchComponent);
